import { useQuery } from '@apollo/client';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Loader from 'components/Loader';
import { logEvent } from 'features/Analytics/analytics';

import {
  OnboardingSasuOffersDocument,
  OnboardingSasuOffersQuery,
} from '../../graphql/queries/onboardingSasuOffers.gql';
import {
  OnboardingPackageItem,
  PricingPlansModuleData,
} from '../../machine/model';
import PlansDesktop from './components/PlansDesktop';
import PlansMobile from './components/PlansMobile';
import getOnboardingPlanDisplay from './lib/getOnboardingPlanDisplay';
import { OnboardingPlanDisplay } from './types';

interface PlanSelectionProps {
  moduleData: PricingPlansModuleData;
  referrer?: string;
  onShowDetails: () => void;
  onChoosePackage: (selectedPackage: OnboardingPackageItem) => void;
  isMobile: boolean;
}

/**
 * A page where the user can see plans main features and select one.
 */
const PlanSelection = asBentoPage<PlanSelectionProps>(
  ({ isMobile, moduleData, onChoosePackage, onPrev, referrer }) => {
    const { viewer } = moduleData;
    const { companyProfileId } = viewer.company;

    const { data } = useQuery<OnboardingSasuOffersQuery>(
      OnboardingSasuOffersDocument,
      {
        variables: {
          companyProfileId,
          input: {
            type: 'CAPITAL_DEPOSIT',
          },
        },
      },
    );

    const capDepAmount = data?.viewer.company.onboardingSasuAddons.find(
      ({ providerAddonId }) => providerAddonId === 'capital-deposit',
    )?.basePriceWithoutVAT;
    const company = data?.viewer.company;

    const packages = company?.onboardingPackages;

    const paymentCardLimits = {
      basicDebitCard: moduleData.basicDebitCardLimits,
      businessWorldDebitCard: moduleData.businessWorldDebitCardLimits,
    };

    if (packages?.length && !capDepAmount) {
      throw new Error('Unable to get capital deposit amount');
    }

    const onboardingPlanDisplays =
      packages?.map((item) =>
        getOnboardingPlanDisplay({
          capDepAmount: capDepAmount as number,
          item: item as OnboardingPackageItem,
          paymentCardLimits,
        }),
      ) ?? [];

    const onChoosePlanDisplay = (planDisplay: OnboardingPlanDisplay) => {
      logEvent({
        name: 'Price Plan Selected',
        properties: {
          isMobile,
          plan: planDisplay.providerPlanId,
          variant: 'solo',
        },
      });

      const selectedPackage =
        packages &&
        (packages?.find(
          ({ providerPackageId }) =>
            providerPackageId === planDisplay.providerPackageId,
        ) as OnboardingPackageItem | undefined);

      if (selectedPackage) {
        return onChoosePackage(selectedPackage);
      } else {
        throw new Error(`Package not found: ${JSON.stringify(planDisplay)}`);
      }
    };

    if (isMobile) {
      return onboardingPlanDisplays.length ? (
        <PlansMobile
          onChoosePlanDisplay={onChoosePlanDisplay}
          onPrev={onPrev}
          planDisplays={onboardingPlanDisplays}
          referrer={referrer}
        />
      ) : (
        <Loader />
      );
    } else {
      return (
        <PlansDesktop
          onboardingPlanDisplays={onboardingPlanDisplays}
          onChoosePlanDisplay={onChoosePlanDisplay}
          onClose={onPrev}
          referrer={referrer}
        />
      );
    }
  },
);

export default PlanSelection;
